/**
 * useStandardUnlock — Checks if the user has met requirements to unlock standard/core mode.
 *
 * Requirements (any ONE must be met):
 * - At least 1 session exists
 * - At least 1 program exists
 *
 * @since Progressive Interface System v2
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface StandardUnlockState {
  isUnlocked: boolean;
  hasSession: boolean;
  hasProgram: boolean;
  loading: boolean;
}

export function useStandardUnlock(): StandardUnlockState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["standard-unlock", user?.id],
    queryFn: async () => {
      if (!user) return { hasSession: false, hasProgram: false };

      const [sessions, programs] = await Promise.all([
        supabase
          .from("sessions" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("programs" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
      ]);

      return {
        hasSession: (sessions.count ?? 0) >= 1,
        hasProgram: (programs.count ?? 0) >= 1,
      };
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  const hasSession = data?.hasSession ?? false;
  const hasProgram = data?.hasProgram ?? false;

  return {
    isUnlocked: hasSession || hasProgram,
    hasSession,
    hasProgram,
    loading: isLoading,
  };
}
